import { Component, computed, effect, inject, signal } from '@angular/core';

import { LangService, RouteWatcher, SeoService } from '@reforgium/presentia';
import { SkSkeletonComponent } from '@ss/ui';

import { DocumentFileDto, DocumentLangs } from 'entities/document.model';
import { AuthService } from 'use-cases/auth.service';
import { DocumentService } from 'use-cases/document.service';
import { DocumentActions } from 'features/actions/document-actions';
import { ExportAction } from 'features/actions/export-actions';
import { DocumentFilePreviewComponent } from 'features/document-file-preview';
import { HistoryPanel } from 'features/panels/history-panel';
import { RedactorComponent } from 'features/redactor';

@Component({
  selector: 'app-document',
  template: `
    <div class="content-container print:grid-cols-1">
      @if (isLoading()) {
        <div class="grid gap-4">
          <div class="flex items-center gap-2 print:hidden">
            <sk-skeleton width="36px" height="36px" />
            <sk-skeleton width="140px" height="20px" />
          </div>
          <div class="flex gap-4">
            <div class="w-full">
              <sk-skeleton width="180px" height="22px" class="mb-4" />
              @for (line of skeletonLines; track $index) {
                <sk-skeleton [width]="line.width" height="16px" class="mb-2" />
              }
            </div>
            <div class="w-full">
              <sk-skeleton width="180px" height="22px" class="mb-4" />
              @for (line of skeletonLines; track $index) {
                <sk-skeleton [width]="line.width" height="16px" class="mb-2" />
              }
            </div>
          </div>
        </div>

        <div class="print:hidden">
          <sk-skeleton width="100%" height="400px" />
        </div>
      } @else {
        <div class="grid gap-4">
          <div class="document-toolbar">
            <app-document-actions
              class="print:hidden"
              title="common.back"
              [versionId]="documentService.publicDocumentVersion.value()?.id"
              [documentId]="documentService.publicDocumentVersion.value()?.documentId"
              [addable]="hasPerm()"
              [editable]="hasPerm()"
              [deletable]="hasPerm()"
            />
            <app-history-panel class="print:hidden" [hasPerm]="hasPerm()">
              <app-export-action [versionId]="this.documentService.publicDocumentVersion.value()?.id" />
            </app-history-panel>
          </div>

          <div class="editors-section">
            <div class="lang-tabs">
              @if (this.langService.currentLang() === 'ru' || this.auth.isAuthenticated) {
                <button
                  type="button"
                  [attr.aria-pressed]="activeTab() === 'ru'"
                  [class.active]="activeTab() === 'ru'"
                  (click)="activeTab.set('ru')"
                >
                  Русский
                </button>
              }
              @if (this.langService.currentLang() === 'ky' || this.auth.isAuthenticated) {
                <button
                  type="button"
                  [attr.aria-pressed]="activeTab() === 'ky'"
                  [class.active]="activeTab() === 'ky'"
                  (click)="activeTab.set('ky')"
                >
                  Кыргызча
                </button>
              }
            </div>

            <div class="editors-row">
              @if (this.langService.currentLang() === 'ru' || this.auth.isAuthenticated) {
                <div class="editor-pane" lang="ru" [class.inactive]="activeTab() !== 'ru'">
                  <div class="flex items-center gap-2 mb-4" [class.sr-only]="!hasPerm()">
                    <h1 class="title-m m-0">{{ docRu()?.title }}</h1>
                  </div>

                  @if (storedRuFile()) {
                    <app-document-file-preview
                      [speechPreview]="true"
                      [storedFile]="storedRuFile()"
                      [versionId]="documentService.publicDocumentVersion.value()?.id"
                    />
                  }

                  @if (!storedRuFile()) {
                    <app-redactor
                      readonly="true"
                      [fitToWidth]="!hasPerm()"
                      [documentTitle]="docRu()?.title ?? ''"
                      [content]="docRu()?.content ?? ''"
                    />
                  }
                </div>
              }

              @if (this.langService.currentLang() === 'ky' || this.auth.isAuthenticated) {
                <div class="editor-pane" lang="ky" [class.inactive]="activeTab() !== 'ky'">
                  <div class="flex items-center gap-2 mb-4" [class.sr-only]="!hasPerm()">
                    <h1 class="title-m m-0">{{ docKy()?.title }}</h1>
                  </div>

                  @if (storedKyFile()) {
                    <app-document-file-preview
                      [speechPreview]="true"
                      [storedFile]="storedKyFile()"
                      [versionId]="documentService.publicDocumentVersion.value()?.id"
                    />
                  }

                  @if (!storedKyFile()) {
                    <app-redactor
                      readonly="true"
                      [fitToWidth]="!hasPerm()"
                      [documentTitle]="docKy()?.title ?? ''"
                      [content]="docKy()?.content ?? ''"
                    />
                  }
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: `
    .content-container {
      width: 100%;
      max-width: none;
      margin-inline: 0;

      @media (max-width: 1024px) {
        grid-template-columns: 1fr;
        max-width: 1200px;
      }
    }

    .document-toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      min-width: 0;
    }
    @media (max-width: 600px) {
      .document-toolbar {
        flex-direction: column;
        align-items: stretch;
      }
    }
    .editors-section {
      padding: 16px;
      background: var(--surface-neutral);
      border-radius: 16px;
      box-shadow: 0 3px 8px #18284405;
      width: 100%;
      margin-inline: 0;
    }

    .editors-row {
      display: flex;
      gap: 1rem;
      flex-direction: row;
    }

    .editors-section,
    .editor-pane {
      min-width: 0;
    }

    .editor-pane {
      flex: 1 1 0;
      width: 0;
    }

    app-redactor {
      display: block;
      width: 100%;
      min-width: 0;
    }

    @media print {
      .editors-section {
        padding: 0;
        box-shadow: none;
      }
    }

    .lang-tabs {
      display: none;
    }

    @media (max-width: 768px) {
      .lang-tabs {
        display: flex;
        margin-bottom: 1rem;

        button {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.625rem 0.5rem;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-secondary);
          border-bottom: 2px solid var(--border-color);
          margin-bottom: -1px;

          &.active {
            color: var(--primary-color);
            border-bottom: 2px solid var(--primary-color);
          }
        }
      }

      .editors-row {
        flex-direction: column;
      }

      .editor-pane {
        flex: 0 0 auto;
        width: 100%;
      }

      .editor-pane.inactive {
        display: none;
      }

      .editor-title {
        display: none;
      }
    }
  `,
  providers: [DocumentService, SeoService],
  imports: [
    RedactorComponent,
    ExportAction,
    HistoryPanel,
    DocumentActions,
    DocumentFilePreviewComponent,
    SkSkeletonComponent,
  ],
})
export class DocumentPage {
  watcher = inject(RouteWatcher);
  langService = inject(LangService);
  documentService = inject(DocumentService);
  auth = inject(AuthService);
  protected activeTab = signal<'ru' | 'ky'>(this.langService.currentLang() === 'ky' ? 'ky' : 'ru');

  docRu = computed(() =>
    this.documentService.publicDocumentVersion.value()?.contents.find((item) => item.language === DocumentLangs.RU),
  );
  docKy = computed(() =>
    this.documentService.publicDocumentVersion.value()?.contents.find((item) => item.language === DocumentLangs.KY),
  );
  storedRuFile = computed(() => this.fileForLanguage(DocumentLangs.RU));
  storedKyFile = computed(() => this.fileForLanguage(DocumentLangs.KY));

  hasPerm = computed(() => this.auth.isAuthenticated);
  isLoading = computed(
    () => this.documentService.publicDocumentVersion.loading() || this.documentService.versions.loading(),
  );
  skeletonLines = [
    { width: '100%' },
    { width: '95%' },
    { width: '88%' },
    { width: '100%' },
    { width: '70%' },
    { width: '92%' },
    { width: '85%' },
    { width: '60%' },
  ];

  constructor() {
    effect(() => {
      const versionId = this.watcher.query()['versionId'];

      if (versionId) {
        void this.documentService.publicDocumentVersion.get({ params: { id: versionId } });
      }
    });
    effect(() => {
      const id = this.watcher.params()['id'];

      if (id) {
        void this.documentService.versions.get({ params: { id } });
      }
    });
    effect(() => {
      const versionId = this.documentService.publicDocumentVersion.value()?.id;

      versionId && void this.documentService.files.get({ params: { versionId } });
    });
  }

  private fileForLanguage(language: DocumentLangs): DocumentFileDto | null {
    return (
      [...(this.documentService.files.value() ?? [])]
        .reverse()
        .find((file) => file.language === language && (file.format === 'PDF' || file.format === 'DOCX')) ?? null
    );
  }
}
